/**
 * OnboardingModal — Modal de profil unifiée
 * Mode `missing` : champs absents uniquement
 * Mode `edit` : édition complète du profil depuis le SaaS
 *
 * @version 2.7.9
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter, Button, Label, BrandLinkRow } from './ui';
import OTPInput from './OTPInput';
import { PhoneInput } from './PhoneInput';
import { profileChangeService } from '../services/profileChange';
import AvatarCropModal from './AvatarCropModal';
import { profileMediaService } from '../services/profileMedia';
import { profileService } from '../services/profile';
import type { NativeUser, UserInfos } from '../types/native';
import { COUNTRIES_SORTED_BY_CODE, DEFAULT_COUNTRY_CODE } from '../utils/countries';
import packageFavicon from '../logo/favicon.png';

const C = {
  primary: '#002147',
  accent: '#e8430a',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray500: '#6b7280',
  gray700: '#374151',
  white: '#ffffff',
} as const;

export interface OnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss: () => void;
  user: NativeUser;
  variant?: 'missing' | 'edit';
  profileHydrating?: boolean;
  hydrateProfile?: boolean;
  onUserInfosSync?: (payload: {
    source: 'hydration' | 'update' | 'avatar' | 'contact';
    user_infos: UserInfos;
  }) => void;
  onComplete: (data: {
    name?: string;
    image_url?: string;
    ccphone?: string;
    phone?: string;
    email?: string;
    address?: string;
    town?: string;
    country?: string;
    user_infos?: UserInfos;
  }) => void;
  onSkip: () => void;
}

type ContactKind = 'email' | 'phone';
type ContactPhase = 'request' | 'verify-old' | 'verify-new';

interface ContactFlowState {
  kind: ContactKind;
  phase: ContactPhase;
  requestId: number | null;
  method: 'email' | 'phone';
  newEmail: string;
  newCcphone: string;
  newPhone: string;
  oldOtp: string;
  newOtp: string;
  loading: boolean;
  error: string;
  resendAvailableAt: number | null;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
type ConfirmAction = 'back' | 'resend' | 'cancel-load' | null;

export function OnboardingModal({
  open,
  onOpenChange,
  onDismiss,
  user,
  variant = 'missing',
  profileHydrating = false,
  hydrateProfile = true,
  onUserInfosSync,
  onComplete,
  onSkip,
}: OnboardingModalProps) {
  const isEditMode = variant === 'edit';
  const [hydratedUser, setHydratedUser] = useState<NativeUser | null>(null);
  const [internalHydrating, setInternalHydrating] = useState(false);
  const hydrationKeyRef = useRef<string | null>(null);
  const profileUser = hydratedUser ?? user;
  const hasCurrentPhone = Boolean(profileUser.phone);
  const hasCurrentEmail = Boolean(profileUser.email);
  const directPhoneEdit = !isEditMode || !hasCurrentPhone;
  const directEmailEdit = !isEditMode || !hasCurrentEmail;
  const needsName = !isEditMode ? !profileUser.name?.trim() : true;

  const [name, setName] = useState(profileUser.name || '');
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [pendingAvatarSrc, setPendingAvatarSrc] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);
  const [ccphone, setCcphone] = useState(profileUser.ccphone || '+221');
  const [phone, setPhone] = useState(profileUser.phone || '');
  const [email, setEmail] = useState(profileUser.email || '');
  const [address, setAddress] = useState(profileUser.address || '');
  const [town, setTown] = useState(profileUser.town || '');
  const [country, setCountry] = useState(profileUser.country || DEFAULT_COUNTRY_CODE);
  const [submitting, setSubmitting] = useState(false);
  const [fileError, setFileError] = useState('');
  const [contactFlow, setContactFlow] = useState<ContactFlowState | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const userHydrationKey = [
    profileUser.reference || '',
    profileUser.name || '',
    profileUser.email || '',
    profileUser.ccphone || '',
    profileUser.phone || '',
    profileUser.address || '',
    profileUser.town || '',
    profileUser.country || '',
    profileUser.image_url || '',
    profileUser.auth_2fa ? '1' : '0',
  ].join('|');
  const currentDialCode = (profileUser.ccphone || ccphone || '+221').trim();
  const baseSmsAllowed = currentDialCode === '+221';
  const contactSmsAllowed = contactFlow
    ? (contactFlow.kind === 'phone'
      ? contactFlow.newCcphone.trim() === '+221'
      : baseSmsAllowed)
    : baseSmsAllowed;
  const lockModalClose = profileHydrating || submitting || submitState === 'success' || avatarUploading || Boolean(contactFlow?.loading);

  const syncFormFromUser = useCallback((nextUser: NativeUser) => {
    setName(nextUser.name || '');
    setPhotoPreview(isEditMode ? (nextUser.image_url || '') : '');
    setCcphone(nextUser.ccphone || '+221');
    setPhone(nextUser.phone || '');
    setEmail(nextUser.email || '');
    setAddress(nextUser.address || '');
    setTown(nextUser.town || '');
    setCountry(nextUser.country || DEFAULT_COUNTRY_CODE);
  }, [isEditMode]);

  const toUserInfos = useCallback((nextUser: NativeUser): UserInfos => ({
    reference: nextUser.reference,
    name: nextUser.name,
    email: nextUser.email || null,
    ccphone: nextUser.ccphone,
    phone: nextUser.phone,
    address: nextUser.address,
    town: nextUser.town,
    country: nextUser.country,
    image_url: nextUser.image_url,
    auth_2fa: nextUser.auth_2fa,
    alias_reference: nextUser.alias_reference,
  }), []);

  useEffect(() => {
    if (!open) {
      setHydratedUser(null);
      hydrationKeyRef.current = null;
      return;
    }

    setPendingAvatarSrc(null);
    setAvatarPickerOpen(false);
    setAvatarUploading(false);
    setSubmitting(false);
    setFileError('');
    setContactFlow(null);
    setSubmitState('idle');
    setSubmitMessage('');
    setSubmitError('');
    setConfirmAction(null);
    syncFormFromUser(profileUser);
  }, [open, userHydrationKey, profileUser, syncFormFromUser]);

  useEffect(() => {
    if (!open || !hydratedUser) return;
    if (contactFlow || submitting || submitState !== 'idle') return;
    syncFormFromUser(hydratedUser);
  }, [open, hydratedUser, contactFlow, submitting, submitState, syncFormFromUser]);

  useEffect(() => {
    if (!open || !hydrateProfile || profileHydrating || internalHydrating || hydratedUser) return;

    const key = userHydrationKey;
    if (hydrationKeyRef.current === key) return;
    hydrationKeyRef.current = key;

    let cancelled = false;
    setInternalHydrating(true);

    (async () => {
      try {
        const response = await profileService.getProfile();
        const payload = (response.user_infos || response.user || {}) as Partial<UserInfos>;
        const nextUser: NativeUser = {
          ...profileUser,
          name: payload.name || profileUser.name,
          email: payload.email ?? profileUser.email ?? null,
          ccphone: payload.ccphone || profileUser.ccphone,
          phone: payload.phone || profileUser.phone,
          address: payload.address || profileUser.address,
          town: payload.town || profileUser.town,
          country: payload.country || profileUser.country,
          image_url: payload.image_url || profileUser.image_url,
          auth_2fa: payload.auth_2fa ?? profileUser.auth_2fa,
          alias_reference: payload.alias_reference || profileUser.alias_reference,
        };

        if (cancelled) return;
        setHydratedUser(nextUser);
        syncFormFromUser(nextUser);
        onUserInfosSync?.({ source: 'hydration', user_infos: toUserInfos(nextUser) });
      } catch {
        if (cancelled) return;
      } finally {
        if (!cancelled) {
          setInternalHydrating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, hydrateProfile, profileHydrating, internalHydrating, userHydrationKey, profileUser, onUserInfosSync, syncFormFromUser, toUserInfos]);

  useEffect(() => {
    if (!contactFlow?.resendAvailableAt) return;

    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [contactFlow?.resendAvailableAt]);

  const markSuccess = useCallback((message: string) => {
    setSubmitting(false);
    setSubmitState('success');
    setSubmitMessage(message);
    setSubmitError('');
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setFileError('Le fichier dépasse 2 Mo. Veuillez choisir une image plus légère.');
      e.target.value = '';
      return;
    }

    setFileError('');
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const canSubmit = !avatarUploading && (
    name.trim().length > 0 &&
    (directPhoneEdit ? phone.length >= 7 : true) &&
    (directEmailEdit ? email.trim().length > 0 : true) &&
    town.trim().length > 0
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitState('submitting');
    setSubmitError('');

    const data = {
      name: name.trim(),
      ccphone: directPhoneEdit ? ccphone : profileUser.ccphone || undefined,
      phone: directPhoneEdit ? phone : profileUser.phone || undefined,
      email: directEmailEdit ? email.trim() : profileUser.email || undefined,
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(town.trim() ? { town: town.trim() } : {}),
      ...(country ? { country } : {}),
    };

    try {
      const response = await profileService.updateProfile(data);
      const payload = (response.user_infos || response.user || {}) as Partial<UserInfos>;
      const updatedUser: NativeUser = {
        ...profileUser,
        name: payload.name || data.name || profileUser.name,
        email: payload.email ?? data.email ?? profileUser.email,
        ccphone: payload.ccphone || data.ccphone || profileUser.ccphone,
        phone: payload.phone || data.phone || profileUser.phone,
        address: payload.address || data.address || profileUser.address,
        town: payload.town || data.town || profileUser.town,
        country: payload.country || data.country || profileUser.country,
        image_url: payload.image_url || profileUser.image_url,
        auth_2fa: payload.auth_2fa ?? profileUser.auth_2fa,
        alias_reference: payload.alias_reference || profileUser.alias_reference,
      };

      setHydratedUser(updatedUser);
      onUserInfosSync?.({ source: 'update', user_infos: toUserInfos(updatedUser) });

      onComplete({
        name: updatedUser.name,
        image_url: updatedUser.image_url,
        ccphone: updatedUser.ccphone,
        phone: updatedUser.phone,
        email: updatedUser.email || undefined,
        address: updatedUser.address,
        town: updatedUser.town,
        country: updatedUser.country,
        user_infos: {
          reference: updatedUser.reference,
          name: updatedUser.name,
          email: updatedUser.email || null,
          ccphone: updatedUser.ccphone,
          phone: updatedUser.phone,
          address: updatedUser.address,
          town: updatedUser.town,
          country: updatedUser.country,
          image_url: updatedUser.image_url,
          auth_2fa: updatedUser.auth_2fa,
          alias_reference: updatedUser.alias_reference,
        },
      });
      markSuccess(response.message || 'Mise à jour réussie');
    } catch (error) {
      setSubmitting(false);
      setSubmitState('error');
      setSubmitError(error instanceof Error ? error.message : 'Erreur lors de l’enregistrement');
    }
  }, [canSubmit, profileUser, name, directPhoneEdit, directEmailEdit, ccphone, phone, email, address, town, country, onComplete, onUserInfosSync, toUserInfos, markSuccess]);

  const openAvatarPicker = useCallback(() => {
    avatarFileInputRef.current?.click();
  }, []);

  const handleAvatarSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingAvatarSrc(reader.result as string);
      setAvatarPickerOpen(true);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleAvatarConfirm = useCallback(async (blob: Blob) => {
    setAvatarUploading(true);
    try {
      const filename = `avatar-${Date.now()}.jpg`;
      const response = await profileMediaService.uploadProfileImage(blob, filename);
      const payload = (response.user_infos || response.user || {}) as Partial<NativeUser & { image?: string }>;
      const nextImage = payload.image_url || payload.image;
      if (nextImage) {
        setPhotoPreview(nextImage);
      } else {
        setPhotoPreview(URL.createObjectURL(blob));
      }
      if (Object.keys(payload).length > 0) {
        const updatedUser: NativeUser = {
          ...profileUser,
          reference: payload.reference || profileUser.reference,
          name: payload.name || profileUser.name,
          email: payload.email ?? profileUser.email ?? null,
          ccphone: payload.ccphone || profileUser.ccphone,
          phone: payload.phone || profileUser.phone,
          address: payload.address || profileUser.address,
          town: payload.town || profileUser.town,
          country: payload.country || profileUser.country,
          image_url: nextImage || profileUser.image_url,
          auth_2fa: payload.auth_2fa ?? profileUser.auth_2fa,
          alias_reference: payload.alias_reference || profileUser.alias_reference,
        };
        setHydratedUser(updatedUser);
        onUserInfosSync?.({ source: 'avatar', user_infos: toUserInfos(updatedUser) });
        onComplete({
          name: payload.name,
          image_url: nextImage || undefined,
          ccphone: payload.ccphone || undefined,
          phone: payload.phone || undefined,
          email: payload.email || undefined,
          address: payload.address || undefined,
          town: payload.town || undefined,
          country: payload.country || undefined,
          user_infos: {
            reference: payload.reference || profileUser.reference,
            name: payload.name || profileUser.name,
            email: payload.email ?? profileUser.email ?? null,
            ccphone: payload.ccphone || profileUser.ccphone,
            phone: payload.phone || profileUser.phone,
            address: payload.address || profileUser.address,
            town: payload.town || profileUser.town,
            country: payload.country || profileUser.country,
            image_url: nextImage || profileUser.image_url,
            auth_2fa: payload.auth_2fa ?? profileUser.auth_2fa,
            alias_reference: payload.alias_reference || profileUser.alias_reference,
          },
        });
      }
      markSuccess('Mise à jour réussie');
      setPendingAvatarSrc(null);
      setAvatarPickerOpen(false);
    } finally {
      setAvatarUploading(false);
    }
  }, [onComplete, onUserInfosSync, toUserInfos, markSuccess, profileUser.reference, profileUser.name, profileUser.email, profileUser.ccphone, profileUser.phone, profileUser.address, profileUser.town, profileUser.country, profileUser.image_url, profileUser.auth_2fa, profileUser.alias_reference]);

  const openContactFlow = useCallback((kind: ContactKind) => {
    setContactFlow({
      kind,
      phase: 'request',
      requestId: null,
      method: kind === 'email'
        ? 'email'
        : (currentDialCode === '+221' ? 'phone' : 'email'),
      newEmail: '',
      newCcphone: kind === 'phone' ? (profileUser.ccphone || '+221') : '+221',
      newPhone: '',
      oldOtp: '',
      newOtp: '',
      loading: false,
      error: '',
      resendAvailableAt: null,
    });
  }, [currentDialCode, profileUser.ccphone]);

  const closeContactFlow = useCallback(() => {
    setContactFlow(null);
  }, []);

  const updateFlow = useCallback((patch: Partial<ContactFlowState>) => {
    setContactFlow((prev) => prev ? { ...prev, ...patch } : prev);
  }, []);

  const currentPhoneDialDisplay = profileUser.ccphone || ccphone || '+221';
  const currentPhoneNumberDisplay = profileUser.phone || phone || '';
  const currentEmailLabel = profileUser.email || email || '';
  const currentPhoneDialCode = (currentPhoneDialDisplay || '+221').trim() || '+221';
  const currentPhoneDigits = (profileUser.phone || phone || '').replace(/\D/g, '');
  const formatPhoneDisplay = useCallback((dial: string, digits: string) => {
    const cleanDial = (dial || '+221').trim() || '+221';
    const cleanDigits = (digits || '').replace(/\D/g, '');
    return cleanDigits ? `${cleanDial} ${cleanDigits}` : cleanDial;
  }, []);
  const currentEmailNormalized = currentEmailLabel.trim().toLowerCase();
  const requestEmailNormalized = contactFlow?.kind === 'email' ? contactFlow.newEmail.trim().toLowerCase() : '';
  const requestPhoneDialCode = contactFlow?.kind === 'phone' ? (contactFlow.newCcphone.trim() || '+221') : '';
  const requestPhoneDigits = contactFlow?.kind === 'phone' ? contactFlow.newPhone.replace(/\D/g, '') : '';
  const emailIsCurrent = Boolean(contactFlow?.kind === 'email' && requestEmailNormalized && requestEmailNormalized === currentEmailNormalized);
  const phoneIsCurrent = Boolean(
    contactFlow?.kind === 'phone'
    && requestPhoneDigits
    && requestPhoneDialCode === currentPhoneDialCode
    && requestPhoneDigits === currentPhoneDigits
  );
  const requestDuplicateMessage = emailIsCurrent
    ? 'Vous ne pouvez pas écrire votre actuelle adresse email. Veuillez en choisir une autre.'
    : phoneIsCurrent
      ? 'Vous ne pouvez pas écrire votre actuel numéro de téléphone. Veuillez en choisir un autre.'
      : '';

  const requestChange = useCallback(async () => {
    if (!contactFlow || contactFlow.loading) return;
    const sameEmail = contactFlow.kind === 'email'
      && contactFlow.newEmail.trim().toLowerCase() === currentEmailNormalized;
    const samePhone = contactFlow.kind === 'phone'
      && contactFlow.newCcphone.trim() === currentPhoneDialCode
      && contactFlow.newPhone.replace(/\D/g, '') === currentPhoneDigits;
    if (sameEmail || samePhone) {
      updateFlow({
        loading: false,
        error: sameEmail
          ? 'Vous ne pouvez pas écrire votre actuelle adresse email. Veuillez en choisir une autre.'
          : 'Vous ne pouvez pas écrire votre actuel numéro de téléphone. Veuillez en choisir un autre.',
      });
      return;
    }
    updateFlow({ loading: true, error: '' });

    try {
      const method = contactSmsAllowed ? contactFlow.method : 'email';
      const response = contactFlow.kind === 'email'
        ? await profileChangeService.requestEmailChange(contactFlow.newEmail.trim(), method)
        : await profileChangeService.requestPhoneChange(contactFlow.newCcphone, contactFlow.newPhone.trim(), method);

      if (!response.success || !response.request_id) {
        throw new Error(response.message || 'Impossible de démarrer le changement');
      }

      updateFlow({ requestId: response.request_id, phase: 'verify-old', loading: false, error: '', resendAvailableAt: Date.now() + 120000 });
    } catch (error) {
      updateFlow({ loading: false, error: error instanceof Error ? error.message : 'Erreur inconnue' });
    }
  }, [contactFlow, updateFlow, contactSmsAllowed, currentEmailNormalized, currentPhoneDialCode, currentPhoneDigits]);

  const verifyOld = useCallback(async () => {
    if (!contactFlow?.requestId || contactFlow.loading) return;
    updateFlow({ loading: true, error: '' });

    try {
      const response = await profileChangeService.verifyOldOTP(contactFlow.requestId, contactFlow.oldOtp);
      if (!response.success) throw new Error(response.message || 'Code OTP incorrect');
      updateFlow({ phase: 'verify-new', loading: false, error: '', resendAvailableAt: Date.now() + 120000 });
    } catch (error) {
      updateFlow({ loading: false, error: error instanceof Error ? error.message : 'Code OTP incorrect' });
    }
  }, [contactFlow, updateFlow]);

  const verifyNew = useCallback(async () => {
    if (!contactFlow?.requestId || contactFlow.loading) return;
    updateFlow({ loading: true, error: '' });

    try {
      const response = await profileChangeService.verifyNewOTP(contactFlow.requestId, contactFlow.newOtp);
      if (!response.success) throw new Error(response.message || 'Code OTP incorrect');

      const updatedUser: NativeUser = {
        ...profileUser,
        ...(contactFlow.kind === 'email'
          ? { email: contactFlow.newEmail.trim() }
          : { ccphone: contactFlow.newCcphone, phone: contactFlow.newPhone.trim() }),
      };

      if (response.user && typeof response.user === 'object') {
        Object.assign(updatedUser, response.user as Partial<NativeUser>);
      }

      setHydratedUser(updatedUser);
      setContactFlow(null);
      onUserInfosSync?.({ source: 'contact', user_infos: toUserInfos(updatedUser) });
      onComplete({
        name: updatedUser.name,
        image_url: updatedUser.image_url,
        ccphone: updatedUser.ccphone,
        phone: updatedUser.phone,
        email: updatedUser.email || undefined,
        user_infos: {
          reference: updatedUser.reference,
          name: updatedUser.name,
          email: updatedUser.email || null,
          ccphone: updatedUser.ccphone,
          phone: updatedUser.phone,
          address: updatedUser.address,
          town: updatedUser.town,
          country: updatedUser.country,
          image_url: updatedUser.image_url,
          auth_2fa: updatedUser.auth_2fa,
          alias_reference: updatedUser.alias_reference,
        },
      });
      markSuccess(contactFlow.kind === 'email' ? 'Email mis à jour avec succès.' : 'Téléphone mis à jour avec succès.');
    } catch (error) {
      updateFlow({ loading: false, error: error instanceof Error ? error.message : 'Code OTP incorrect' });
    }
  }, [contactFlow, onComplete, onUserInfosSync, updateFlow, profileUser, toUserInfos, markSuccess]);

  const resendFlowOtp = useCallback(async () => {
    if (!contactFlow?.requestId || contactFlow.loading) return;
    updateFlow({ loading: true, error: '' });

    try {
      const response = await profileChangeService.resendOTP(contactFlow.requestId);
      if (!response.success) throw new Error(response.message || 'Impossible de renvoyer le code');
      updateFlow({ loading: false, error: '', resendAvailableAt: Date.now() + 120000 });
    } catch (error) {
      updateFlow({ loading: false, error: error instanceof Error ? error.message : 'Erreur inconnue' });
    }
  }, [contactFlow, updateFlow]);

  const switchFlowMethod = useCallback(async (method: 'email' | 'phone') => {
    if (!contactFlow) return;

    if (contactFlow.phase === 'request' || !contactFlow.requestId) {
      updateFlow({ method, error: '' });
      return;
    }

    if (contactFlow.loading) return;
    updateFlow({ loading: true, error: '', method });

    try {
      const response = await profileChangeService.switchMethod(contactFlow.requestId, method);
      if (!response.success) throw new Error(response.message || 'Impossible de changer la méthode');
      updateFlow({ loading: false, error: '' });
    } catch (error) {
      updateFlow({ loading: false, error: error instanceof Error ? error.message : 'Erreur inconnue' });
    }
  }, [contactFlow, updateFlow]);

  useEffect(() => {
    if (!contactFlow || contactFlow.kind !== 'phone') return;
    if (contactFlow.newCcphone.trim() !== '+221' && contactFlow.method !== 'email') {
      updateFlow({ method: 'email' });
    }
  }, [contactFlow, updateFlow]);

  const confirmBackToRequest = useCallback(() => {
    if (!contactFlow) return;
    updateFlow({
      phase: 'request',
      requestId: null,
      oldOtp: '',
      newOtp: '',
      error: '',
      loading: false,
      resendAvailableAt: null,
    });
    setConfirmAction(null);
  }, [contactFlow, updateFlow]);

  const confirmResendOtp = useCallback(async () => {
    if (!contactFlow) return;
    setConfirmAction(null);
    await resendFlowOtp();
  }, [contactFlow, resendFlowOtp]);

  const handleDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    if (lockModalClose) return;
    onOpenChange(false);
  }, [lockModalClose, onOpenChange]);
  const contactTargetLabel = contactFlow
    ? (contactFlow.kind === 'email'
      ? (contactFlow.newEmail.trim() || currentEmailLabel || 'votre adresse email')
      : formatPhoneDisplay(contactFlow.newCcphone, contactFlow.newPhone))
    : '';
  const currentReceiveLabel = (contactFlow ? (contactSmsAllowed ? contactFlow.method : 'email') : 'email') === 'phone'
    ? `SMS au ${contactFlow?.kind === 'phone' ? contactTargetLabel : formatPhoneDisplay(currentPhoneDialDisplay, currentPhoneNumberDisplay)}`
    : `email à ${contactFlow?.kind === 'email' ? contactTargetLabel : (currentEmailLabel || 'votre adresse email')}`;
  const resendCountdown = contactFlow?.resendAvailableAt
    ? Math.max(0, Math.ceil((contactFlow.resendAvailableAt - nowTick) / 1000))
    : 0;
  const canResendOtp = Boolean(contactFlow?.requestId) && !contactFlow?.loading && resendCountdown === 0;

  const ShieldIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );

  const renderSuccess = () => (
    <>
      <DialogHeader>
        <BrandLinkRow
          icon={<ShieldIcon />}
          faviconSrc={packageFavicon}
          faviconAlt="Ollaid SSO"
          iconBg="rgba(232, 67, 10, 0.1)"
          iconColor={C.accent}
        />
        <DialogTitle>Profil enregistré</DialogTitle>
        <DialogDescription>
          {submitMessage || 'Vos informations ont été mises à jour.'}
        </DialogDescription>
      </DialogHeader>

      <DialogBody style={{ maxHeight: '52vh', overflowY: 'auto', paddingBottom: '0.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '1.5rem 1rem', textAlign: 'center' }}>
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: C.gray700 }}>
            Mise à jour réussie
          </div>
          <div style={{ fontSize: '0.875rem', color: C.gray500 }}>
            Vous pouvez fermer cette fenêtre maintenant.
          </div>
        </div>
      </DialogBody>
      <DialogFooter style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Button
          type="button"
          variant="outline"
          onClick={onDismiss}
          style={{ width: '100%', height: '2.6rem' }}
        >
          Fermer
        </Button>
      </DialogFooter>
    </>
  );

  const renderHydration = () => (
    <>
      <DialogHeader>
        <BrandLinkRow
          icon={<ShieldIcon />}
          faviconSrc={packageFavicon}
          faviconAlt="Ollaid SSO"
          iconBg="rgba(232, 67, 10, 0.1)"
          iconColor={C.accent}
        />
        <DialogTitle>{isEditMode ? 'Modifier votre profil' : 'Complétez votre profil'}</DialogTitle>
        <DialogDescription>
          Récupération de vos infos depuis iam.ollaid.com en cours. Le SSO synchronise vos données avant l’affichage du formulaire.
        </DialogDescription>
      </DialogHeader>

      <DialogBody style={{ maxHeight: '52vh', overflowY: 'auto', paddingBottom: '0.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.9rem', padding: '2rem 1rem', textAlign: 'center' }}>
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: C.gray700 }}>
            Récupération de vos informations
          </div>
          <div style={{ fontSize: '0.875rem', color: C.gray500, lineHeight: 1.5 }}>
            Le modal sera rempli automatiquement dès que les données IAM seront prêtes.
          </div>
        </div>
      </DialogBody>

      <DialogFooter style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          type="button"
          variant="outline"
          onClick={() => setConfirmAction('cancel-load')}
          style={{ minWidth: '6.5rem', height: '2.35rem' }}
        >
          Fermer
        </Button>
      </DialogFooter>
    </>
  );

  const renderBase = () => (
    <>
      <DialogHeader>
        <BrandLinkRow
          icon={<ShieldIcon />}
          faviconSrc={packageFavicon}
          faviconAlt="Ollaid SSO"
          iconBg="rgba(232, 67, 10, 0.1)"
          iconColor={C.accent}
        />
        <DialogTitle>{isEditMode ? 'Modifier votre profil' : 'Complétez votre profil'}</DialogTitle>
        <DialogDescription>
          {isEditMode
            ? 'Mettez à jour vos informations sans quitter le SaaS.'
            : 'Ajoutez les informations manquantes pour finaliser votre compte.'}
        </DialogDescription>
      </DialogHeader>

      <DialogBody style={{ maxHeight: '52vh', overflowY: 'auto', paddingBottom: '0.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginTop: '0.25rem', paddingRight: '0.25rem' }}>
          {submitError && (
            <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.875rem' }}>
              {submitError}
            </div>
          )}

          {submitState === 'submitting' && (
            <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Enregistrement en cours...
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '4.25rem 1fr', gap: '0.75rem', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={openAvatarPicker}
                disabled={avatarUploading}
                style={{
                  width: '4.25rem',
                  height: '4.25rem',
                  borderRadius: '50%',
                  border: `1px solid ${C.gray200}`,
                  backgroundColor: C.gray100,
                  overflow: 'hidden',
                  position: 'relative',
                  padding: 0,
                  cursor: avatarUploading ? 'not-allowed' : 'pointer',
                }}
              >
                {photoPreview || profileUser.image_url ? (
                  <img src={photoPreview || profileUser.image_url} alt="Photo de profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray500 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M5.5 21a8.38 8.38 0 0 1 13 0" />
                    </svg>
                  </div>
                )}
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: avatarUploading ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.08)',
                }}>
                  {avatarUploading ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 8h.01" />
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                  )}
                </div>
              </button>
              <input ref={avatarFileInputRef} type="file" accept="image/*" onChange={handleAvatarSelected} style={{ display: 'none' }} />
            </div>

            <div>
              <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                Nom complet
              </Label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jean Dupont"
                style={{
                  width: '100%',
                  height: '2.25rem',
                  padding: '0 0.75rem',
                  border: `1px solid ${C.gray200}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: C.gray700,
                  backgroundColor: C.white,
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {isEditMode && hasCurrentPhone && !contactFlow && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                <Label style={{ color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                  Numéro de téléphone
                </Label>
                <button type="button" onClick={() => openContactFlow('phone')} style={{ border: 'none', background: 'transparent', padding: 0, color: C.accent, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  Changer
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '6rem 1fr', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={currentPhoneDialDisplay}
                  readOnly
                  style={{
                    width: '100%',
                    height: '2.25rem',
                    padding: '0 0.65rem',
                    border: `1px solid ${C.gray200}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: C.gray700,
                    backgroundColor: '#f9fafb',
                    outline: 'none',
                  }}
                />
                <input
                  type="text"
                  value={currentPhoneNumberDisplay}
                  readOnly
                  style={{
                    width: '100%',
                    height: '2.25rem',
                    padding: '0 0.75rem',
                    border: `1px solid ${C.gray200}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: C.gray700,
                    backgroundColor: '#f9fafb',
                    outline: 'none',
                  }}
                />
              </div>
            </div>
          )}

          {isEditMode && hasCurrentEmail && !contactFlow && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                <Label style={{ color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                  Adresse email
                </Label>
                <button type="button" onClick={() => openContactFlow('email')} style={{ border: 'none', background: 'transparent', padding: 0, color: C.accent, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  Changer
                </button>
              </div>
              <input
                type="email"
                value={currentEmailLabel}
                readOnly
                style={{
                  width: '100%',
                  height: '2.25rem',
                  padding: '0 0.75rem',
                  border: `1px solid ${C.gray200}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: C.gray700,
                  backgroundColor: '#f9fafb',
                  outline: 'none',
                }}
              />
            </div>
          )}

          {(!isEditMode || !hasCurrentPhone) && (
            <div>
              <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                Numéro de téléphone
              </Label>
              <PhoneInput value={phone} onChange={setPhone} ccphone={ccphone} onCcphoneChange={setCcphone} />
            </div>
          )}

          {(!isEditMode || !hasCurrentEmail) && (
            <div>
              <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                Adresse email
              </Label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemple.com"
                style={{
                  width: '100%',
                  height: '2.25rem',
                  padding: '0 0.75rem',
                  border: `1px solid ${C.gray200}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: C.gray700,
                  backgroundColor: C.white,
                  outline: 'none',
                }}
              />
            </div>
          )}

          <div style={{ display: 'grid', gap: '0.85rem' }}>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                Adresse
              </Label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rue, quartier, numéro"
                style={{
                  width: '100%',
                  height: '2.25rem',
                  padding: '0 0.75rem',
                  border: `1px solid ${C.gray200}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: C.gray700,
                  backgroundColor: C.white,
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                  Ville <span style={{ color: '#dc2626' }}>*</span>
                </Label>
                <input
                  type="text"
                  value={town}
                  onChange={(e) => setTown(e.target.value)}
                  required
                  placeholder="Dakar"
                  style={{
                    width: '100%',
                    height: '2.25rem',
                    padding: '0 0.75rem',
                    border: `1px solid ${C.gray200}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: C.gray700,
                    backgroundColor: C.white,
                    outline: 'none',
                  }}
                />
              </div>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.35rem', color: C.gray700, fontWeight: 500, fontSize: '0.875rem' }}>
                  Pays
                </Label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  style={{
                    width: '100%',
                    height: '2.25rem',
                    padding: '0 0.6rem',
                    border: `1px solid ${C.gray200}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: C.gray700,
                    backgroundColor: C.white,
                    outline: 'none',
                  }}
                >
                  {COUNTRIES_SORTED_BY_CODE.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

        </div>
      </DialogBody>

      <DialogFooter style={{ flexDirection: 'row', justifyContent: submitState === 'success' ? 'center' : 'space-between', alignItems: 'center' }}>
        {submitState === 'success' ? (
          <Button
            type="button"
            variant="outline"
            onClick={onDismiss}
            style={{ width: '100%', height: '2.6rem' }}
          >
            Fermer
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              style={{ minWidth: '6.5rem', height: '2.35rem' }}
            >
              Fermer
            </Button>
            <Button
              type="button"
              onClick={isEditMode ? handleSubmit : onSkip}
              disabled={isEditMode ? (!canSubmit || submitting) : submitting}
              style={{ minWidth: '7.25rem', height: '2.35rem', opacity: (isEditMode ? (canSubmit && !submitting) : !submitting) ? 1 : 0.5 }}
            >
              {submitting ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Enregistrement...
                </span>
              ) : (isEditMode ? 'Enregistrer' : 'Plus tard')}
            </Button>
          </>
        )}
      </DialogFooter>
      <AvatarCropModal
        open={avatarPickerOpen}
        imageSrc={pendingAvatarSrc}
        onOpenChange={setAvatarPickerOpen}
        onCancel={() => {
          setAvatarPickerOpen(false);
          setPendingAvatarSrc(null);
        }}
        onConfirm={handleAvatarConfirm}
      />
    </>
  );

  const renderContactFlow = () => {
    if (!contactFlow) return null;
    const isEmail = contactFlow.kind === 'email';
    const targetLabel = isEmail
      ? (contactFlow.newEmail || 'la nouvelle adresse')
      : `${contactFlow.newCcphone} ${contactFlow.newPhone}`.trim();
    const sentToLabel = currentReceiveLabel;

    return (
      <>
        <DialogHeader>
          <BrandLinkRow
            icon={<ShieldIcon />}
            faviconSrc={packageFavicon}
            faviconAlt="Ollaid SSO"
            iconBg="rgba(232, 67, 10, 0.1)"
            iconColor={C.accent}
          />
          <DialogTitle>{isEmail ? 'Changer l’email' : 'Changer le téléphone'}</DialogTitle>
          <DialogDescription>
            {contactFlow.phase === 'request'
              ? `Veuillez renseigner le nouveau ${isEmail ? 'email' : 'numéro de téléphone'} pour lancer la vérification.`
              : contactFlow.phase === 'verify-old'
                ? `Le code OTP a été envoyé sur : ${sentToLabel}.`
                : `Le second code OTP a été envoyé sur : ${sentToLabel}.`
            }
          </DialogDescription>
        </DialogHeader>

        <DialogBody style={{ maxHeight: '52vh', overflowY: 'auto', paddingBottom: '0.25rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.75rem' }}>
            {contactFlow.error && (
              <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.875rem' }}>
                {contactFlow.error}
              </div>
            )}

            {contactFlow.phase === 'request' && (
              <>
                {isEmail ? (
                  <div>
                    <div style={{ marginBottom: '0.5rem', padding: '0.7rem 0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.85rem', color: C.gray700, lineHeight: 1.45 }}>
                      <div><strong>Email actuel :</strong> {currentEmailLabel || 'non renseigné'}</div>
                      <div style={{ marginTop: '0.2rem', color: C.gray500 }}>
                        Veuillez renseigner le nouveau email pour lancer la vérification.
                      </div>
                    </div>
                    <Label style={{ display: 'block', marginBottom: '0.5rem', color: C.gray700, fontWeight: 500 }}>
                      Nouvel email
                    </Label>
                    <input
                      type="email"
                      value={contactFlow.newEmail}
                      onChange={(e) => updateFlow({ newEmail: e.target.value })}
                      placeholder="nouvel@email.com"
                      style={{
                        width: '100%',
                        height: '2.5rem',
                        padding: '0 0.75rem',
                        border: `1px solid ${C.gray200}`,
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: C.gray700,
                        backgroundColor: C.white,
                        outline: 'none',
                      }}
                      />
                    {emailIsCurrent && (
                      <span style={{ display: 'block', marginTop: '0.35rem', color: '#b91c1c', fontSize: '0.8rem', lineHeight: 1.35 }}>
                        {requestDuplicateMessage}
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: '0.5rem', padding: '0.7rem 0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.85rem', color: C.gray700, lineHeight: 1.45 }}>
                      <div><strong>Numéro actuel :</strong> {formatPhoneDisplay(currentPhoneDialDisplay, currentPhoneNumberDisplay) || 'non renseigné'}</div>
                      <div style={{ marginTop: '0.2rem', color: C.gray500 }}>
                        Veuillez renseigner le nouveau numéro de téléphone pour lancer la vérification.
                      </div>
                    </div>
                    <Label style={{ display: 'block', marginBottom: '0.5rem', color: C.gray700, fontWeight: 500 }}>
                      Nouveau téléphone
                    </Label>
                    <PhoneInput
                      value={contactFlow.newPhone}
                      onChange={(value) => updateFlow({ newPhone: value })}
                      ccphone={contactFlow.newCcphone}
                      onCcphoneChange={(value) => updateFlow({ newCcphone: value, method: value.trim() === '+221' ? contactFlow.method : 'email' })}
                    />
                    {phoneIsCurrent && (
                      <span style={{ display: 'block', marginTop: '0.35rem', color: '#b91c1c', fontSize: '0.8rem', lineHeight: 1.35 }}>
                        {requestDuplicateMessage}
                      </span>
                    )}
                  </div>
                )}

                {contactSmsAllowed ? (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      variant={contactFlow.method === 'email' ? 'default' : 'outline'}
                      onClick={() => switchFlowMethod('email')}
                      style={{ flex: 1 }}
                    >
                      Code par email
                    </Button>
                    <Button
                      type="button"
                      variant={contactFlow.method === 'phone' ? 'default' : 'outline'}
                      onClick={() => switchFlowMethod('phone')}
                      style={{ flex: 1 }}
                    >
                      Code par téléphone
                    </Button>
                  </div>
                ) : (
                  <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', color: C.gray700, fontSize: '0.875rem', lineHeight: 1.45 }}>
                    Le SMS est disponible uniquement pour les numéros sénégalais (+221). Le code sera envoyé par email.
                  </div>
                )}
                <span style={{ color: C.gray500, fontSize: '0.8rem', lineHeight: 1.35 }}>
                  Vous allez recevoir le code par {currentReceiveLabel}.
                </span>
              </>
            )}

            {contactFlow.phase === 'verify-old' && (
              <>
                <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.875rem', color: C.gray700 }}>
                  <div><strong>{isEmail ? 'Email actuel' : 'Numéro de téléphone actuel'} :</strong> {isEmail ? currentEmailLabel : formatPhoneDisplay(currentPhoneDialDisplay, currentPhoneNumberDisplay)}</div>
                  <div style={{ marginTop: '0.25rem' }}><strong>Nouveau :</strong> {targetLabel}</div>
                  <div style={{ marginTop: '0.25rem' }}><strong>Envoi :</strong> {sentToLabel}</div>
                </div>

                <div>
                  <Label style={{ display: 'block', marginBottom: '0.5rem', color: C.gray700, fontWeight: 500 }}>
                    Code reçu sur l'ancien contact
                  </Label>
                  <OTPInput
                    value={contactFlow.oldOtp}
                    onChange={(value) => updateFlow({ oldOtp: value })}
                    disabled={contactFlow.loading}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.82rem', color: C.gray500 }}>
                    {canResendOtp
                      ? 'Vous pouvez renvoyer un nouveau code.'
                      : `Renvoyer un code dans ${resendCountdown}s`}
                  </div>
                  {canResendOtp && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConfirmAction('resend')}
                      disabled={contactFlow.loading}
                      style={{ height: '2.35rem' }}
                    >
                      Renvoyer le code
                    </Button>
                  )}
                </div>
              </>
            )}

            {contactFlow.phase === 'verify-new' && (
              <>
                <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.875rem', color: C.gray700 }}>
                  <div><strong>{isEmail ? 'Email actuel' : 'Numéro de téléphone actuel'} :</strong> {isEmail ? currentEmailLabel : formatPhoneDisplay(currentPhoneDialDisplay, currentPhoneNumberDisplay)}</div>
                  <div style={{ marginTop: '0.25rem' }}><strong>Nouveau :</strong> {targetLabel}</div>
                  <div style={{ marginTop: '0.25rem' }}><strong>Envoi :</strong> {sentToLabel}</div>
                </div>

                <div>
                  <Label style={{ display: 'block', marginBottom: '0.5rem', color: C.gray700, fontWeight: 500 }}>
                    Code reçu sur le nouveau contact
                  </Label>
                  <OTPInput
                    value={contactFlow.newOtp}
                    onChange={(value) => updateFlow({ newOtp: value })}
                    disabled={contactFlow.loading}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.82rem', color: C.gray500 }}>
                    {canResendOtp
                      ? 'Vous pouvez renvoyer un nouveau code.'
                      : `Renvoyer un code dans ${resendCountdown}s`}
                  </div>
                  {canResendOtp && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConfirmAction('resend')}
                      disabled={contactFlow.loading}
                      style={{ height: '2.35rem' }}
                    >
                      Renvoyer le code
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogBody>
        <DialogFooter style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          {contactFlow.phase === 'request' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={closeContactFlow}
                disabled={contactFlow.loading}
                style={{ minWidth: '6.5rem', height: '2.35rem' }}
              >
                Fermer
              </Button>
              <Button
                type="button"
                onClick={requestChange}
                disabled={
                  contactFlow.loading ||
                  (isEmail ? !contactFlow.newEmail.trim() : !contactFlow.newPhone.trim()) ||
                  emailIsCurrent ||
                  phoneIsCurrent
                }
                style={{ minWidth: '7.25rem', height: '2.35rem', opacity: contactFlow.loading ? 0.5 : 1 }}
              >
                {contactFlow.loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Envoi...
                  </span>
                ) : 'Envoyer'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmAction('back')}
                disabled={contactFlow.loading}
                style={{ minWidth: '6.5rem', height: '2.35rem' }}
              >
                Retour
              </Button>
              <Button
                type="button"
                onClick={contactFlow.phase === 'verify-old' ? verifyOld : verifyNew}
                disabled={
                  contactFlow.loading ||
                  (contactFlow.phase === 'verify-old' && contactFlow.oldOtp.length !== 6) ||
                  (contactFlow.phase === 'verify-new' && contactFlow.newOtp.length !== 6)
                }
                style={{ minWidth: '7.25rem', height: '2.35rem', opacity: contactFlow.loading ? 0.5 : 1 }}
              >
                {contactFlow.loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Vérification...
                  </span>
                ) : (contactFlow.phase === 'verify-old' ? 'Vérifier' : 'Confirmer')}
              </Button>
            </>
          )}
        </DialogFooter>
      </>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmAction) return null;

    const config = confirmAction === 'back'
      ? {
          title: 'Retourner à l’étape précédente ?',
          description: 'La vérification en cours sera interrompue. Vous pourrez reprendre le changement depuis le début.',
          confirmLabel: 'Retourner',
        }
      : confirmAction === 'resend'
        ? {
          title: 'Renvoyer un nouveau code OTP ?',
          description: `Un nouveau code sera envoyé vers ${currentReceiveLabel}.`,
          confirmLabel: 'Renvoyer',
          }
        : {
          title: 'Annuler la récupération ?',
          description: 'Les informations IAM ne seront pas récupérées maintenant. Vous pourrez relancer l’ouverture plus tard.',
          confirmLabel: 'Annuler',
        };

    const confirm = async () => {
      if (confirmAction === 'back') {
        confirmBackToRequest();
      } else if (confirmAction === 'resend') {
        await confirmResendOtp();
      } else {
        setConfirmAction(null);
        onDismiss();
      }
    };

    return (
      <Dialog open={Boolean(confirmAction)} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
          <DialogContent hideCloseButton={false} style={{ maxWidth: '22rem', padding: '0.9rem 1rem', borderRadius: '0.7rem' }}>
          <DialogHeader>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>{config.title}</div>
            <DialogDescription>{config.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button type="button" variant="outline" onClick={() => setConfirmAction(null)} style={{ minWidth: '6rem', height: '2.25rem' }}>
              Annuler
            </Button>
            <Button type="button" onClick={confirm} style={{ minWidth: '6.5rem', height: '2.25rem' }}>
              {confirmAction === 'resend' && contactFlow?.loading ? 'Envoi...' : config.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        hideCloseButton={lockModalClose}
        style={{ maxWidth: '26rem', padding: '0.75rem 0.8rem', borderRadius: '0.65rem', maxHeight: '88vh' }}
      >
        {profileHydrating && submitState === 'idle' && !contactFlow ? renderHydration() : contactFlow ? renderContactFlow() : submitState === 'success' ? renderSuccess() : renderBase()}
      </DialogContent>
      {renderConfirmDialog()}
    </Dialog>
  );
}

export default OnboardingModal;
